(function () {
  'use strict';

  const QUESTION_STREAM_STATUS_MIN_MS = 420;

  function prefetchNextQuestion(instance) {
    if (instance._prefetchedQuestion) return instance._prefetchedQuestion;
    instance._prefetchedQuestion = startQuestionFetch(instance);
    instance._prefetchedRequestId = instance._requestId;
    return instance._prefetchedQuestion;
  }

  function buildQuestionRequestBody(instance, locale) {
    return {
      conversation_history: instance.conversationHistory,
      session_id: instance._ensureSessionId(),
      provider: instance.config.modelProvider || 'openai',
      ...instance._buildWorkerContextPayload(locale),
      answer: instance._buildLatestAnswerPayload(),
      debug: !!instance.config.debug
    };
  }

  function getQuestionStreamStatusCopy(instance, eventName) {
    const t = instance.config?.i18n || {};
    const fallback = t.analyzing || 'Reviewing what you shared...';
    const map = {
      accepted: t.questionStreamAccepted || fallback,
      retrieving_corpus: t.questionStreamRetrieving || fallback,
      building_question: t.questionStreamBuilding || fallback,
      model_output_started: t.questionStreamWriting || t.questionStreamBuilding || fallback,
      model_output_progress: t.questionStreamWriting || t.questionStreamBuilding || fallback,
      final_question: t.questionStreamFinalizing || fallback
    };
    return map[eventName] || '';
  }

  function resetQuestionStreamStatus(instance) {
    const state = instance._questionStreamStatusState;
    if (state && state.timer) {
      clearTimeout(state.timer);
    }

    instance._questionStreamStatusState = {
      queue: [],
      timer: null,
      lastShownAt: 0,
      lastText: ''
    };

    return instance._questionStreamStatusState;
  }

  function getQuestionStreamStatusState(instance) {
    return instance._questionStreamStatusState || resetQuestionStreamStatus(instance);
  }

  function applyQuestionStreamStatus(instance, loadingText, text) {
    const state = getQuestionStreamStatusState(instance);
    loadingText.textContent = text;
    state.lastText = text;
    state.lastShownAt = Date.now();
  }

  function pumpQuestionStreamStatus(instance, loadingText) {
    const state = getQuestionStreamStatusState(instance);
    if (state.timer || !state.queue.length) return;

    const elapsed = state.lastShownAt ? Date.now() - state.lastShownAt : QUESTION_STREAM_STATUS_MIN_MS;
    const waitMs = Math.max(QUESTION_STREAM_STATUS_MIN_MS - elapsed, 0);

    const showNext = function showNextStatus() {
      state.timer = null;
      const nextText = state.queue.shift();
      if (!nextText) return;

      if (instance.currentStep === 'question-loading' && document.contains(loadingText)) {
        applyQuestionStreamStatus(instance, loadingText, nextText);
      }
      pumpQuestionStreamStatus(instance, loadingText);
    };

    if (waitMs > 0) {
      state.timer = setTimeout(showNext, waitMs);
    } else {
      showNext();
    }
  }

  function renderQuestionStreamEvent(instance, eventName) {
    const text = getQuestionStreamStatusCopy(instance, eventName);
    if (!text) return;
    const loadingText = instance.querySelector('[data-step="question-loading"] .consultation-loading__text');
    if (!loadingText) return;

    const state = getQuestionStreamStatusState(instance);

    if (eventName === 'final_question') {
      if (state.timer) clearTimeout(state.timer);
      state.timer = null;
      state.queue = [];
      applyQuestionStreamStatus(instance, loadingText, text);
      return;
    }

    const queuedText = state.queue[state.queue.length - 1] || '';
    if (state.lastText === text || queuedText === text) return;

    state.queue.push(text);
    pumpQuestionStreamStatus(instance, loadingText);
  }

  function shouldFallbackFromStream(error) {
    if (!error || error.name === 'AbortError') return false;
    if (error.streamFinalError) return false;
    if (error.status === 429) return false;
    const code = String(error.errorCode || error.code || '').trim();
    if (code.indexOf('rate_limited_') === 0) return false;
    return Boolean(error.streamHttpFailure || error.streamUnavailable || error.streamProtocolError);
  }

  function postQuestionRequest(instance, body, requestController, finishQuestionTimer, reqId) {
    resetQuestionStreamStatus(instance);
    const latencyTargetMs = 20000;
    const baseUrl = instance.config.workerUrl;
    const canStream = instance.config.questionStreaming !== false
      && window.TaraConsultationApi
      && typeof window.TaraConsultationApi.postEventStream === 'function';

    if (!canStream) {
      return window.TaraConsultationApi.postJson({
        baseUrl,
        path: '/api/question',
        body,
        signal: requestController.signal,
        fallbackMessage: 'Question request failed',
        slowThresholdMs: latencyTargetMs,
        timerExtra: { request_id: reqId, transport: 'json' },
        startTimer: () => finishQuestionTimer
      });
    }

    return window.TaraConsultationApi.postEventStream({
      baseUrl,
      path: '/api/question-stream',
      body,
      signal: requestController.signal,
      fallbackMessage: 'Question stream failed',
      slowThresholdMs: latencyTargetMs,
      timerExtra: { request_id: reqId, transport: 'stream' },
      startTimer: () => finishQuestionTimer,
      onEvent: (eventName) => renderQuestionStreamEvent(instance, eventName)
    }).catch((error) => {
      if (!shouldFallbackFromStream(error)) throw error;
      instance._log('question-stream-fallback', {
        reqId,
        error: error?.message || String(error),
        status: error?.status || null
      });
      return window.TaraConsultationApi.postJson({
        baseUrl,
        path: '/api/question',
        body,
        signal: requestController.signal,
        fallbackMessage: 'Question request failed',
        slowThresholdMs: latencyTargetMs,
        timerExtra: { request_id: reqId, transport: 'json_fallback' },
        startTimer: () => finishQuestionTimer
      });
    });
  }

  function startQuestionFetch(instance) {
    if (!instance.config.workerUrl) {
      console.warn('[Tara Consultation] workerUrl is empty');
      return Promise.resolve(null);
    }

    const reqId = ++instance._requestId;
    instance._inflight = true;
    instance._setInflightUI(true);

    if (instance.abortController) {
      try {
        instance.abortController.abort('stale-request');
      } catch (error) {
        instance.abortController.abort();
      }
    }

    const requestController = new AbortController();
    instance.abortController = requestController;
    const abortForTimeout = () => {
      try {
        requestController.abort('request-timeout');
      } catch (error) {
        requestController.abort();
      }
    };
    const timeout = setTimeout(abortForTimeout, 60000);
    const finishQuestionTimer = instance._startMonitoringTimer('question_fetch', {
      route: '/api/question',
      tags: { consultation_action: 'question_fetch' }
    });
    let questionDurationMs = 0;

    const url = window.TaraConsultationApi && typeof window.TaraConsultationApi.resolveUrl === 'function'
      ? window.TaraConsultationApi.resolveUrl('/api/question', instance.config.workerUrl)
      : `${instance.config.workerUrl}/api/question`;
    const locale = instance._getUiLocale();
    const requestBody = buildQuestionRequestBody(instance, locale);
    console.log('[Tara Consultation] Fetching ->', url, { reqId });

    return postQuestionRequest(instance, requestBody, requestController, finishQuestionTimer, reqId)
      .then(({ response, data, durationMs }) => {
        clearTimeout(timeout);
        questionDurationMs = durationMs || questionDurationMs;
        console.log('[Tara Consultation] Response status:', response.status, { reqId });
        if (reqId !== instance._requestId) {
          instance._log('response-stale', { reqId, current: instance._requestId });
          return null;
        }
        return data;
      })
      .then((data) => {
        if (reqId !== instance._requestId) {
          instance._log('response-stale', { reqId, current: instance._requestId });
          return null;
        }
        if (data && data.session_id) {
          instance.sessionId = data.session_id;
          instance._setStoredSessionId(data.session_id);
        }
        // The Worker rides the store's rolling average along on the question
        // response — no extra round trip. Absent (cold record, KV blip) means
        // the generation meter keeps its shipped default.
        if (data && data.timing && typeof data.timing === 'object') {
          instance._servedTimings = data.timing;
        }
        if (data && typeof instance._persistProgressState === 'function') {
          instance._persistProgressState(data.question_id || '');
        }
        console.log('[Tara Consultation] Question received:', data?.question_id, { complete: data?.complete });
        return data;
      })
      .catch((error) => {
        clearTimeout(timeout);
        questionDurationMs = error?.requestDurationMs || questionDurationMs;

        if (requestController.signal.aborted) {
          if (reqId !== instance._requestId || requestController !== instance.abortController) {
            instance._log('request-aborted-stale', { reqId });
            return null;
          }

          const abortReason = requestController.signal.reason || 'aborted';
          instance._log('request-aborted-active', { reqId, reason: abortReason });
          if (abortReason === 'request-timeout') {
            window.TaraConsultationMonitoring?.addBreadcrumb?.('question_fetch request timed out', {
              request_id: reqId,
              request_duration_ms: questionDurationMs,
              abort_reason: abortReason
            }, 'warning');
          }
          throw error;
        }

        console.error('[Tara Consultation] Fetch failed:', error.message);
        instance._captureMonitoringException(error, {
          route: '/api/question',
          error_code: error?.errorCode || 'question_request_failed',
          status_code: error?.status || null,
          request_duration_ms: questionDurationMs,
          worker_error: error?.workerError || error?.message || '',
          tags: { consultation_action: 'question_fetch' },
          extra: { request_id: reqId },
          fingerprint: ['tara-consultation', 'question-fetch', error?.errorCode || error?.name || 'error']
        });
        throw error;
      })
      .finally(() => {
        if (reqId === instance._requestId && requestController === instance.abortController) {
          instance._inflight = false;
          instance._setInflightUI(false);
        }
      });
  }

  async function requestNextQuestion(instance, direction = 'forward', requestPromise = null, requestId = null) {
    instance._showBridgeText();
    instance._showStep('question-loading', direction);

    if (!instance.config.workerUrl) {
      console.warn('[Tara Consultation] worker URL is not configured');
      instance._renderQuestionRequestError(direction);
      return;
    }

    const pendingRequest = requestPromise || startQuestionFetch(instance);
    const reqId = requestId || instance._requestId;

    instance._log('request-start', { reqId });

    try {
      const data = await pendingRequest;
      if (reqId !== instance._requestId) {
        instance._log('response-stale', { reqId, current: instance._requestId });
        return;
      }

      if (!data) {
        instance._renderQuestionRequestError(direction);
        return;
      }

      instance._lastAdaptiveResponse = data;
      instance._lastQuestionComplete = data.complete || false;
      instance._log('response-ok', { reqId, questionId: data.question_id, complete: data.complete });

      if (data.already_completed === true) {
        if (window.TaraConsultationCompletion && typeof window.TaraConsultationCompletion.handleAlreadyCompletedResponse === 'function') {
          window.TaraConsultationCompletion.handleAlreadyCompletedResponse(instance, data);
        } else {
          instance._renderQuestionRequestError(direction);
        }
        return;
      }

      if (data.complete) {
        instance._enterContactCapture(data.wait_experience || null, direction);
      } else {
        instance._renderAdaptiveQuestion(data);
        instance._showStep('adaptive', direction);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        if (reqId !== instance._requestId) return;
        console.warn('[Tara Consultation] Question fetch timed out');
        instance._renderQuestionRequestError(direction, error);
        return;
      }
      if (reqId !== instance._requestId) return;
      console.error('[Tara Consultation] Question fetch failed:', error);
      instance._renderQuestionRequestError(direction, error);
    }
  }

  function advanceFromPrefetch(instance, direction = 'forward') {
    const pendingRequest = instance._prefetchedQuestion || startQuestionFetch(instance);
    const requestId = instance._prefetchedQuestion ? instance._prefetchedRequestId : instance._requestId;
    instance._prefetchedQuestion = null;
    instance._prefetchedRequestId = 0;
    return requestNextQuestion(instance, direction, pendingRequest, requestId);
  }

  function fetchNextQuestion(instance, direction = 'forward') {
    instance._prefetchedQuestion = null;
    instance._prefetchedRequestId = 0;
    return requestNextQuestion(instance, direction);
  }

  window.TaraConsultationQuestionFlow = Object.freeze({
    prefetchNextQuestion,
    startQuestionFetch,
    requestNextQuestion,
    advanceFromPrefetch,
    fetchNextQuestion,
  });
})();
